// FIRST: every fetch finds its edge function by the path (utils/apiRoutes). The
// money family lives in mos-money since 2026-10-23; the ~270 API_BASE lines
// still name make-server, and this is what sends them to the right place.
import { installApiRouter } from "./utils/apiRoutes";
installApiRouter();

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import { ErrorBoundary } from "./components/shared/ErrorBoundary";
import { APP_VERSION, APP_VERSION_LS_KEY } from "./utils/version";
import { LAST_LOCATION_KEY } from "./utils/lastLocation";
import { FLOOR_SESSION_KEY } from "./utils/floorSession";
import { KIOSK_MODE_KEY, WATER_SPIDER_KIOSK_MODE_KEY, applyKioskLaunch } from "./components/inventory/kioskMode";
import "./index.css";
import { keepFieldAboveKeyboard } from "./utils/keepFieldAboveKeyboard";

// The box you type in stays above a tablet's keyboard (keepFieldAboveKeyboard.ts).
keepFieldAboveKeyboard();

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("Root element not found");

// ── Clean-first-run reset ──────────────────────────────────────
// The first time a NEW build runs on this browser, start it from a
// clean slate so no stale cached state leaks across versions. We
// deliberately PRESERVE:
//   • sb-*  → the Supabase auth session (so a deploy never forces a re-login)
//   • the user-preference keys below
//   • IndexedDB mos-offline-v1 (may hold unsynced offline plant work)
// Runs once per new version; subsequent loads of the same version
// early-return. Must run before createRoot so providers don't hydrate
// from soon-to-be-cleared state.
const PRESERVE_LS_KEYS = new Set<string>([
  "mos_exec_dashboard_layout_v1", // executive dashboard layout (user pref)
  "mos_rate_review",              // estimating rate defaults (user pref)
  "mos:agent-chain-rules",        // agent chain config (user pref; also KV-backed)
  // Each person's own dashboard arrangement (components/hr/dashboardLayout):
  // wiped, every deploy would put everyone's sections back the way they shipped.
  "mos:my-dashboard-layout",
  // Where the user was standing. NOT stale app state — it's the breadcrumb the
  // update reload exists to honor, and an update is exactly when this reset
  // runs. Wiping it here is what sent every update back to My Profile. The
  // restore re-validates the view against the NEW build, so a screen this
  // version dropped just falls back to the default landing.
  LAST_LOCATION_KEY,
  // The shop tablet's department screen + who is signed in on it. The idle
  // auto-update is the main reason a kiosk ever reloads, so this is exactly
  // the state that reset must carry across (see utils/floorSession).
  FLOOR_SESSION_KEY,
  // The shop's inventory touchscreen and the water spider's screen by the
  // casting lines each stay on their kiosk through an update
  // (components/inventory/kioskMode).
  KIOSK_MODE_KEY,
  WATER_SPIDER_KIOSK_MODE_KEY,
  // Which tablet or browser this is, for the live seats the plant screens
  // share (utils/plantLive PLANT_DEVICE_KEY). A device keeps its identity
  // across an update, so the seat it holds is still its own after the reload.
  'mos:device-id',
  // A Plaid OAuth handshake is in flight across a full browser redirect to the
  // bank. If a deploy lands in that window, wiping this strands the return —
  // the bank says yes and MOS has nothing left to finish with.
  'mos_plaid_oauth_handoff_v1',
  // How a person opens the PM board — lanes, or the single-lane preview
  // (components/pm/single/pmBoardLayout). The profile holds the real choice;
  // this copy is what the board paints first, and a deploy must not flip
  // a person back to the lanes before their next profile read.
  'mos.pm.boardLayout',
]);

function runCleanFirstRunReset() {
  let stored: string | null = null;
  try { stored = localStorage.getItem(APP_VERSION_LS_KEY); } catch { return; }
  if (stored === APP_VERSION) return; // normal run — same version

  try {
    // sessionStorage holds THIS TAB's location snapshot (the authority for a
    // refresh). Carry it across the wipe; everything else — including the
    // chunk-reload guard — still gets reset.
    let keepLocation: string | null = null;
    try { keepLocation = sessionStorage.getItem(LAST_LOCATION_KEY); } catch { /* ignore */ }
    sessionStorage.clear(); // no auth here; also resets the chunk-reload guard
    if (keepLocation) {
      try { sessionStorage.setItem(LAST_LOCATION_KEY, keepLocation); } catch { /* ignore */ }
    }
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k) continue;
      if (k.startsWith("sb-")) continue;       // preserve Supabase auth → no re-login
      if (k === APP_VERSION_LS_KEY) continue;  // our own marker
      if (PRESERVE_LS_KEYS.has(k)) continue;   // preserve user settings
      toRemove.push(k);
    }
    toRemove.forEach((k) => localStorage.removeItem(k));
    // IndexedDB mos-offline-v1 (unsynced offline queue) intentionally preserved.
  } catch { /* private mode / storage disabled — ignore */ }

  try { localStorage.setItem(APP_VERSION_LS_KEY, APP_VERSION); } catch { /* ignore */ }
}

runCleanFirstRunReset();
// A kiosk PC opens /?kiosk=inventory or /?kiosk=water-spider: put it on its
// kiosk before the app reads where it was (components/inventory/kioskMode).
applyKioskLaunch(window.location.href);

// Surface unhandled promise rejections to the console so silent failures
// (fetch, auth, lazy-import) don't just produce a blank UI state.
window.addEventListener('unhandledrejection', (ev) => {
  console.error('[unhandledrejection]', ev.reason);
});
window.addEventListener('error', (ev) => {
  console.error('[window.onerror]', ev.error ?? ev.message);
});

createRoot(rootElement).render(
  <StrictMode>
    <ErrorBoundary scope="App root">
      <App />
    </ErrorBoundary>
  </StrictMode>
);

// ── Service worker cleanup ─────────────────────────────────────
// The D-05 cache-first SW pinned browsers to stale builds and caused
// the white-screen reports. We replaced it with a kill-switch SW
// (public/sw.js) that self-unregisters on activate. Here we also run
// a belt-and-suspenders cleanup on every page load: unregister any
// SW we find and nuke all caches. This guarantees every client
// eventually escapes the stale-cache death spiral even if they never
// pick up the new kill-switch SW.
//
// Offline support will come back later with a network-first HTML
// strategy so redeploys can't brick the app.
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations()
    .then((regs) => {
      regs.forEach((reg) => {
        reg.unregister().catch(() => { /* ignore */ });
      });
    })
    .catch(() => { /* ignore */ });

  if ('caches' in window) {
    caches.keys()
      .then((keys) => Promise.all(keys.map((k) => caches.delete(k))))
      .catch(() => { /* ignore */ });
  }
}
